// src/components/panoramica/lineupOptimizer.js
// Sceglie modulo e undici che massimizzano il fantapunteggio ATTESO della
// prossima giornata, partendo dalle previsioni di forecastEngine:
//   - ogni titolare vale p(gioca)·fantavoto + (1 − p)·riserva che entra al
//     suo posto (le sostituzioni automatiche del fantacalcio);
//   - + modificatore di difesa atteso (solo con almeno 4 difensori);
//   - + extra del capitano / vice (bonus sopra la sufficienza raddoppiato).
// Classic: per ogni modulo i migliori per reparto. Mantra: assegnazione
// ottima giocatore → slot del modulo (metodo ungherese) rispettando i ruoli.
import { MANTRA_PRESETS } from '../auction/MantraRoleConfiguration';
import { expectedDefenseModifier } from './forecastEngine';

const CLASSIC_PRESETS = {
  '3-5-2': { P: 1, D: 3, C: 5, A: 2 },
  '3-4-3': { P: 1, D: 3, C: 4, A: 3 },
  '4-3-3': { P: 1, D: 4, C: 3, A: 3 },
  '4-4-2': { P: 1, D: 4, C: 4, A: 2 },
  '4-5-1': { P: 1, D: 4, C: 5, A: 1 },
  '5-3-2': { P: 1, D: 5, C: 3, A: 2 },
  '5-4-1': { P: 1, D: 5, C: 4, A: 1 },
};
// Stessi moduli del tab Formazione (non importati da lì per non trascinare
// Firebase in un modulo di puro calcolo).
const CLASSIC_OPTIONS = Object.keys(CLASSIC_PRESETS);
const MANTRA_OPTIONS = Object.keys(MANTRA_PRESETS);
const ROLE_KEYS = ['P', 'D', 'C', 'A'];
const MANTRA_TOKEN_RE = /(Pc|Dd|Dc|Ds|B|E|M|C|W|T|A|P)/g;

export function mantraTokensOf(item) {
  const snap = item?.purchase?.playerSnapshot || {};
  const raw = [snap.mantraRole, item?.purchase?.mantraRole, snap.role, item?.purchase?.role].find((x) => String(x || '').trim());
  return [...new Set(String(raw || '').match(MANTRA_TOKEN_RE) || [])];
}

// Valore di un titolare dato chi entrerebbe al suo posto (solo per le
// visualizzazioni: il totale usa expectedWithBench, che conta ogni riserva
// una volta sola).
const starterValue = (f, subValue) => f.pPlay * f.expFV + (1 - f.pPlay) * subValue;
const subValueOf = (f) => (f ? f.pPlay * f.expFV : 0);

// Valore atteso di un gruppo di titolari con le riserve dietro.
// Le riserve entrano IN ORDINE e ognuna al massimo una volta: la k-esima
// riserva gioca solo se mancano almeno k titolari. Il numero di assenti segue
// una binomiale "di Poisson" sulle probabilità di non giocare di ciascuno.
// (Errore corretto il 2026-10-21: contare la stessa riserva come cambio di
// OGNI titolare a rischio faceva sembrare conveniente lasciare in panchina un
// giocatore sicuro di giocare — es. Thuram — "come assicurazione".)
export function expectedWithBench(starters, benchSorted) {
  let dist = [1]; // dist[k] = P(k titolari assenti)
  let own = 0;
  starters.forEach((x) => {
    const f = x.forecast;
    own += f.pPlay * f.expFV;
    const q = 1 - f.pPlay;
    const next = new Array(dist.length + 1).fill(0);
    dist.forEach((pk, k) => { next[k] += pk * (1 - q); next[k + 1] += pk * q; });
    dist = next;
  });
  let atLeast = 1; // P(K ≥ k)
  let subs = 0;
  benchSorted.forEach((b, i) => {
    atLeast -= dist[i] || 0;
    if (atLeast > 1e-9) subs += atLeast * subValueOf(b.forecast);
  });
  return { own, subs, total: own + subs };
}

function captainExtra(starters, rules) {
  if (!rules.captain || !starters.length) return { extra: 0, captain: null, vice: null };
  // Extra del capitano: il fantavoto sopra il 6 conta doppio.
  const gain = (f) => Math.max(0, f.expFV - 6);
  const ranked = [...starters].sort((a, b) => b.forecast.pPlay * gain(b.forecast) - a.forecast.pPlay * gain(a.forecast));
  const c = ranked[0]; const vc = rules.viceCaptain ? ranked[1] : null;
  const pc = c.forecast.pPlay;
  const extra = pc * gain(c.forecast) + (vc ? (1 - pc) * vc.forecast.pPlay * gain(vc.forecast) : 0);
  return { extra, captain: c, vice: vc };
}

function totalVariance(starters) {
  return starters.reduce((s, x) => {
    const f = x.forecast;
    return s + f.pPlay * f.sd ** 2 + f.pPlay * (1 - f.pPlay) * f.expFV ** 2 * 0.25;
  }, 0);
}

function finalize({ formationKey, rolesMode, starters, bench, complete, rules, rows = null, base }) {
  const gk = starters.find((s) => s.forecast.role === 'P');
  const defs = starters.filter((s) => s.forecast.role === 'D');
  const defMod = expectedDefenseModifier(gk?.forecast, defs.map((d) => d.forecast), rules);
  const cap = captainExtra(starters, rules);
  const total = base + defMod.expected + cap.extra;
  const sd = Math.sqrt(totalVariance(starters));
  return {
    formationKey, rolesMode, starters, bench, rows, complete,
    base, defenseModifier: defMod, captain: cap.captain, vice: cap.vice, captainExtra: cap.extra,
    total, low: total - 1.2816 * sd, high: total + 1.2816 * sd,
  };
}

// ─── Classic ────────────────────────────────────────────────────────────────
function buildClassic(items, formationKey, rules) {
  const preset = CLASSIC_PRESETS[formationKey];
  const byRole = {};
  ROLE_KEYS.forEach((r) => { byRole[r] = items.filter((i) => i.forecast.role === r); });
  const starters = []; const bench = []; let complete = true; let base = 0;
  ROLE_KEYS.forEach((r) => {
    const need = preset[r];
    const pool = [...byRole[r]].sort((a, b) => b.forecast.value - a.forecast.value);
    // Ricerca esatta (le rose sono piccole: al massimo qualche centinaio di
    // combinazioni per reparto): per ogni insieme di titolari si calcola il
    // valore atteso con le riserve del reparto che entrano in ordine, ognuna
    // una volta sola, solo se qualcuno manca davvero (expectedWithBench).
    const cand = pool.slice(0, Math.min(pool.length, 10));
    let bestSet = cand.slice(0, need);
    let bestVal = -Infinity;
    const evalSet = (set) => {
      const ids = new Set(set.map((x) => x.forecast.playerId));
      return expectedWithBench(set, pool.filter((x) => !ids.has(x.forecast.playerId))).total;
    };
    const pick = (start, acc) => {
      if (acc.length === need) {
        const val = evalSet(acc);
        if (val > bestVal) { bestVal = val; bestSet = [...acc]; }
        return;
      }
      for (let i = start; i <= cand.length - (need - acc.length); i++) { acc.push(cand[i]); pick(i + 1, acc); acc.pop(); }
    };
    if (need > 0 && cand.length >= need) pick(0, []);
    const chosenIds = new Set(bestSet.map((x) => x.forecast.playerId));
    const rest = pool.filter((x) => !chosenIds.has(x.forecast.playerId));
    const sub = subValueOf(rest[0]?.forecast);
    const setVal = bestSet.length ? evalSet(bestSet) : 0;
    base += setVal;
    bestSet
      .sort((a, b) => b.forecast.expFV - a.forecast.expFV)
      .forEach((x) => {
        // Quanto si perderebbe scambiandolo con la miglior riserva del reparto,
        // con lo STESSO calcolo usato per scegliere: è il numero mostrato nel
        // confronto "perché lui e non lui".
        const alt = rest[0];
        const swapGap = alt ? setVal - evalSet(bestSet.map((y) => (y === x ? alt : y))) : null;
        starters.push({ ...x, rowKey: r, value: starterValue(x.forecast, sub), swapAltId: alt?.forecast.playerId ?? null, swapGap });
      });
    rest.forEach((x) => bench.push(x));
    if (bestSet.length < need) complete = false;
  });
  return finalize({ formationKey, rolesMode: 'classic', starters, bench, complete, rules, base });
}

// ─── Mantra: assegnazione ottima (ungherese) ────────────────────────────────
// cost[n slot][m giocatori], n ≤ m. Ritorna per ogni slot l'indice giocatore.
function hungarian(cost) {
  const n = cost.length; const m = cost[0]?.length || 0;
  const INF = 1e9;
  const u = new Array(n + 1).fill(0); const vv = new Array(m + 1).fill(0);
  const p = new Array(m + 1).fill(0); const way = new Array(m + 1).fill(0);
  for (let i = 1; i <= n; i++) {
    p[0] = i; let j0 = 0;
    const minv = new Array(m + 1).fill(INF); const used = new Array(m + 1).fill(false);
    do {
      used[j0] = true; const i0 = p[j0]; let delta = INF; let j1 = 0;
      for (let j = 1; j <= m; j++) {
        if (used[j]) continue;
        const cur = cost[i0 - 1][j - 1] - u[i0] - vv[j];
        if (cur < minv[j]) { minv[j] = cur; way[j] = j0; }
        if (minv[j] < delta) { delta = minv[j]; j1 = j; }
      }
      for (let j = 0; j <= m; j++) {
        if (used[j]) { u[p[j]] += delta; vv[j] -= delta; } else minv[j] -= delta;
      }
      j0 = j1;
    } while (p[j0] !== 0);
    do { const j1 = way[j0]; p[j0] = p[j1]; j0 = j1; } while (j0);
  }
  const res = new Array(n).fill(-1);
  for (let j = 1; j <= m; j++) if (p[j]) res[p[j] - 1] = j - 1;
  return res;
}

function buildMantra(items, formationKey, rules) {
  const preset = MANTRA_PRESETS[formationKey];
  const slots = [];
  preset.rows.forEach((row) => row.slots.forEach((options) => slots.push({ rowKey: row.key, rowTitle: row.title, options })));
  const tokens = items.map(mantraTokensOf);
  const BIG = 1000;
  // Giocatori "fittizi" per gli slot che nessuno può coprire (modulo incompleto).
  const m = Math.max(items.length, slots.length);
  const cost = slots.map((s) => Array.from({ length: m }, (_, j) => {
    if (j >= items.length) return BIG; // slot vuoto
    return s.options.some((o) => tokens[j].includes(o)) ? -items[j].forecast.value : BIG;
  }));
  const assign = items.length ? hungarian(cost) : slots.map(() => -1);
  const starters = []; const used = new Set(); let complete = true;
  const rows = preset.rows.map((row) => ({ key: row.key, title: row.title, players: [] }));
  assign.forEach((j, si) => {
    const slot = slots[si];
    const ok = j >= 0 && j < items.length && cost[si][j] < BIG;
    if (!ok) { complete = false; return; }
    used.add(j);
    const x = { ...items[j], rowKey: slot.rowKey, slotLabel: slot.options.join('/'), value: items[j].forecast.value };
    starters.push(x);
    rows.find((r) => r.key === slot.rowKey).players.push(x);
  });
  const bench = items.filter((_, j) => !used.has(j)).sort((a, b) => b.forecast.value - a.forecast.value);
  // Valore con le riserve: per reparto, ognuna una volta sola (vedi
  // expectedWithBench). L'assegnazione agli slot usa solo p·FV, quindi qui
  // non c'è il rischio di "tenere fuori" un titolare sicuro.
  let base = 0;
  ['P', 'D', 'C', 'A'].forEach((r) => {
    const st = starters.filter((x) => x.forecast.role === r);
    if (!st.length) return;
    base += expectedWithBench(st, bench.filter((b) => b.forecast.role === r)).total;
  });
  starters.forEach((x) => {
    const slot = slots.find((s) => s.options.join('/') === x.slotLabel && s.rowKey === x.rowKey);
    const sub = bench.find((b) => slot.options.some((o) => mantraTokensOf(b).includes(o)));
    x.value = starterValue(x.forecast, subValueOf(sub?.forecast));
  });
  return finalize({ formationKey, rolesMode: 'mantra', starters, bench, complete, rules, rows, base });
}

// items: [{ purchase, forecast }]. Ritorna il migliore + la classifica moduli.
export function optimizeLineup({ items = [], rolesMode = 'classic', rules }) {
  const options = rolesMode === 'mantra' ? MANTRA_OPTIONS : CLASSIC_OPTIONS;
  const candidates = options
    .filter((k) => (rolesMode === 'mantra' ? MANTRA_PRESETS[k] : CLASSIC_PRESETS[k]))
    .map((k) => (rolesMode === 'mantra' ? buildMantra(items, k, rules) : buildClassic(items, k, rules)));
  const ranked = [...candidates].sort((a, b) => (Number(b.complete) - Number(a.complete)) || b.total - a.total);
  return {
    best: ranked[0] || null,
    // Tutte le formazioni calcolate, per mostrarne una qualsiasi sul campo.
    byKey: Object.fromEntries(candidates.map((c) => [c.formationKey, c])),
    modules: ranked.map((c) => ({
      key: c.formationKey, total: c.total, base: c.base, defMod: c.defenseModifier.expected,
      captainExtra: c.captainExtra, complete: c.complete,
    })),
  };
}

// ─── "Perché lui e non lui" ─────────────────────────────────────────────────
// Confronta un titolare con l'alternativa migliore rimasta fuori: differenza
// per fattore (in punti di fantavoto attesi) + probabilità di giocare.
export function compareForecasts(starter, alternative) {
  const a = starter.forecast; const b = alternative.forecast;
  const byKey = (f) => Object.fromEntries(f.factors.map((x) => [x.key, x]));
  const fa = byKey(a); const fb = byKey(b);
  const diffs = ['base', 'form', 'opponent', 'venue', 'bonus'].map((k) => ({
    key: k, label: fa[k]?.label || fb[k]?.label, diff: (fa[k]?.value || 0) - (fb[k]?.value || 0),
    starterLabel: fa[k]?.label, altLabel: fb[k]?.label,
  }));
  diffs.push({ key: 'availability', label: 'Probabilità di giocare', diff: (a.pPlay - b.pPlay) * 6, pp: a.pPlay - b.pPlay });
  return {
    diffs: diffs.sort((x, y) => Math.abs(y.diff) - Math.abs(x.diff)),
    // Guadagno atteso di schierare A con B in panchina, rispetto al contrario.
    // Se A non gioca entra B (e viceversa), quindi:
    //   [pA·FA + (1−pA)·pB·FB] − [pB·FB + (1−pB)·pA·FA] = pA·pB·(FA − FB)
    // → tra due che si coprono a vicenda conviene chi rende di più QUANDO
    // gioca; la probabilità di giocare conta solo quando manca il cambio.
    valueGap: a.pPlay * b.pPlay * (a.expFV - b.expFV),
    expGap: a.expFV - b.expFV,
    starterLessLikely: a.pPlay < b.pPlay - 0.05,
  };
}
